import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { cacheable, cacheGet, cachePut, offlineNow, isNetErr, NEEDS_SIGNAL } from "./idb";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const LOGIN_AS_KEY = "c369-login-as";
export const LOGIN_AS = { company: { label: "Company team", roles: ["admin", "staff"] }, employer: { label: "Principal Employer", roles: ["employer"] }, contractor: { label: "Contractor", roles: ["vendor"] } };
export const loginGroup = (role) => Object.keys(LOGIN_AS).find((k) => LOGIN_AS[k].roles.includes(role));

export function useApi() {
  const { getToken } = useAuth();
  return useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use(async (c) => {
      const t = await getToken();
      if (t) c.headers.Authorization = `Bearer ${t}`;
      const as = localStorage.getItem(LOGIN_AS_KEY);
      if (as) c.headers["X-Login-As"] = as;
      return c;
    });
    return inst;
  }, [getToken]);
}

export function useFetch(path) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const latest = useRef(path);
  latest.current = path;
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    const live = () => latest.current === path;
    const fromCache = async () => {
      const c = cacheable(path) ? await cacheGet(path) : undefined;
      if (!live()) return;
      setData(c ?? null); setError(c === undefined ? NEEDS_SIGNAL : null); setStale(c !== undefined);
    };
    if (offlineNow()) { await fromCache(); setLoading(false); return; }
    try {
      const d = (await api.get(path)).data;
      if (cacheable(path)) cachePut(path, d);
      if (!live()) return;
      setData(d); setError(null); setStale(false);
    } catch (e) { if (isNetErr(e)) await fromCache(); else if (live()) { setData(null); setError(errMsg(e)); } }
    if (live()) setLoading(false);
  }, [api, path]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    ["c369-synced", "online"].forEach((ev) => window.addEventListener(ev, reload));
    return () => ["c369-synced", "online"].forEach((ev) => window.removeEventListener(ev, reload));
  }, [reload]);
  return { data, loading, error, reload, setData, stale };
}

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join("; ");
  return d || e?.message || "Something went wrong";
};

export const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const lastMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export function calcPreview(days, gross, basic) {
  const g = Number(gross) || 0, b = Number(basic) || 0, dd = Number(days) || 0;
  const ceiling = new Date() >= new Date("2026-09-17") ? 25000 : 15000;
  const pf = Math.round(Math.min(Math.max(b, g * 0.5), ceiling) * 0.12 * 100) / 100;
  const ok = g <= 21000;
  const ee = ok && (!dd || g / dd > 176) ? Math.round(g * 0.0075 * 100) / 100 : 0;
  const er = ok ? Math.round(g * 0.0325 * 100) / 100 : 0;
  return { pf, ee, er, net: g - pf - ee, breach: dd > 0 && g / dd < 450, daily: dd ? g / dd : 0 };
}
